import { t, ui, uiError } from '../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  AccountTree as LedgerIcon,
  CheckCircle as CheckCircleIcon,
  DataObject as PayloadIcon,
  Hub as HubIcon,
  Key as KeyIcon,
  Link as LinkIcon,
  Rule as RuleIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { getWebappApiUrl, webappFetch } from '../../utils/apiConfig';
import { isValidationOperation, operationDisplayLabel } from '../../domain/integrationLedger';

type PartnerKey = 'EINS' | 'KAIST' | 'KULS';
type LedgerDirection = 'source' | 'target';

type UserInfo = {
  username: string;
  displayName: string;
  role: string;
  orgName: string;
  orgIcon: string;
  loginTime: string;
};

type PartnerInfoConfig = {
  displayName: string;
  roleSummary: string;
  organizationName: string;
  mspLabel: string;
  ledgerScope: string;
  org: PartnerKey;
  direction: LedgerDirection;
  endpoint: string;
  endpointStatus: string;
  requiredFields: string[];
  preservedFields: string[];
  mappingRows: Array<[string, string]>;
  visibleOperations: string[];
  color: string;
  accent: string;
};

type SummaryResponse = {
  summary: Record<string, any>;
  operationBreakdown: Array<{ operation: string; count: number }>;
  recentTransactions: Array<{
    block_number: string;
    sender: string | null;
    receiver: string | null;
    business_id: string | null;
    business_id_type: string | null;
    operation_type: string | null;
    block_created_at: string | null;
  }>;
};

const PARTNER_INFO: Record<PartnerKey, PartnerInfoConfig> = {
  EINS: {
    displayName: 'eINS',
    roleSummary: 'Dispatch optimization result provider for KULS-bound records.',
    organizationName: 'eINS',
    mspLabel: 'eINS / EINS',
    ledgerScope: 'sourceOrg = EINS',
    org: 'EINS',
    direction: 'source',
    endpoint: 'POST /backend/api/v1/integration/eins/dispatch-optimization-results',
    endpointStatus: 'Active endpoint for eINS dispatch optimization result submission.',
    requiredFields: ['scenario.scenario_id', 'scenario.scen_type'],
    preservedFields: ['best', 'best_schedule', 'sim_log', 'metadata', 'passthrough fields'],
    mappingRows: [
      ['sourceOrg', 'EINS'],
      ['targetOrg', 'KULS'],
      ['recordType', 'EINS_DISPATCH_OPTIMIZATION_RESULT'],
      ['businessId', 'payload.scenario.scenario_id'],
      ['operationType', 'payload.scenario.scen_type'],
      ['payloadHash / payloadRef', 'Gateway-calculated hash-ref evidence']
    ],
    visibleOperations: ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt'],
    color: '#1976d2',
    accent: '#e3f2fd'
  },
  KAIST: {
    displayName: 'KAIST',
    roleSummary: 'Yard optimization result provider for KULS-bound records.',
    organizationName: 'Korea Advanced Institute of Science and Technology',
    mspLabel: 'KAIST / KAIST',
    ledgerScope: 'sourceOrg = KAIST',
    org: 'KAIST',
    direction: 'source',
    endpoint: 'POST /backend/api/v1/integration/kaist/yard-optimization-results',
    endpointStatus: 'Active endpoint for KAIST yard optimization result submission.',
    requiredFields: ['request_id', 'operation'],
    preservedFields: ['status', 'yard_id', 'yard_name', 'moves', 'summary', 'generated_at', 'passthrough fields'],
    mappingRows: [
      ['sourceOrg', 'KAIST'],
      ['targetOrg', 'KULS'],
      ['recordType', 'KAIST_YARD_OPTIMIZATION_RESULT'],
      ['businessId', 'payload.request_id'],
      ['operationType', 'payload.operation normalized to lower-case'],
      ['payloadHash / payloadRef', 'Gateway-calculated hash-ref evidence']
    ],
    visibleOperations: ['assignment', 'retrieval', 'relocation'],
    color: '#5e35b1',
    accent: '#ede7f6'
  },
  KULS: {
    displayName: 'KULS',
    roleSummary: 'Receiver and reviewer of partner result records from eINS and KAIST.',
    organizationName: 'KULS',
    mspLabel: 'KULS / KULS',
    ledgerScope: 'targetOrg = KULS',
    org: 'KULS',
    direction: 'target',
    endpoint: 'No dedicated KULS partner-result invoke endpoint in the current flow',
    endpointStatus: 'KULS reviews committed records submitted by eINS and KAIST.',
    requiredFields: ['Not applicable for the current KULS review flow'],
    preservedFields: ['eINS dispatch records', 'KAIST yard optimization records'],
    mappingRows: [
      ['visibleScope', 'targetOrg = KULS'],
      ['eINS recordType', 'EINS_DISPATCH_OPTIMIZATION_RESULT'],
      ['KAIST recordType', 'KAIST_YARD_OPTIMIZATION_RESULT'],
      ['businessId', 'scenario_id or request_id depending on source partner'],
      ['operationType', 'Daily_Opt / Emergency_Opt / Validation_Opt / assignment / retrieval / relocation'],
      ['payloadHash / payloadRef', 'Read from committed partner records']
    ],
    visibleOperations: ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt', 'assignment', 'retrieval', 'relocation'],
    color: '#2e7d32',
    accent: '#e8f5e9'
  }
};

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US');
}

function asNumber(value: unknown) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function operationCount(data: SummaryResponse | null, operation: string) {
  return asNumber(data?.operationBreakdown?.find((item) => item.operation === operation)?.count);
}

async function fetchSummary(config: PartnerInfoConfig): Promise<SummaryResponse> {
  const params = new URLSearchParams({
    fromBlock: '0',
    recentLimit: '3',
    org: config.org,
    direction: config.direction,
    businessOnly: 'true'
  });
  const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain/integration-prototype/summary?${params.toString()}`);
  const body = await response.json();
  if (!response.ok || !body.success) {
    throw new Error(body.message || body.error || `HTTP ${response.status}`);
  }
  return body.data;
}

function InfoCard({
  title,
  icon,
  children
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          {icon}
          <Typography variant="subtitle1" fontWeight={800}>
            {ui(title)}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function MetricCard({ label, value, helper }: { label: string; value: string; helper?: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: 2,
        backgroundColor: '#fff',
        minHeight: 96
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
        {ui(label)}
      </Typography>
      <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {ui(helper)}
        </Typography>
      )}
    </Paper>
  );
}

function KeyValue({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {ui(label)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.35,
          fontWeight: 650,
          fontFamily: mono ? 'monospace' : 'inherit',
          overflowWrap: 'anywhere'
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ChipGroup({ fields, color = 'default' }: { fields: string[]; color?: 'default' | 'primary' | 'success' }) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {fields.map((field) => (
        <Chip key={field} size="small" color={color} variant={color === 'default' ? 'outlined' : 'filled'} label={ui(field)} />
      ))}
    </Stack>
  );
}

export function PartnerMyInfoPage({ partner, user }: { partner: PartnerKey; user: UserInfo }) {
  const config = PARTNER_INFO[partner];
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      setError(null);
      setSummary(await fetchSummary(config));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load organization summary');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    loadSummary();
    const timer = window.setInterval(loadSummary, 5000);
    return () => window.clearInterval(timer);
  }, [loadSummary]);

  const latestRecord = summary?.recentTransactions?.[0] || null;
  const businessRecords = asNumber(summary?.summary.business_records);
  const hashRefRecords = asNumber(summary?.summary.hash_ref_records);
  const latestBlock = summary?.summary.latest_indexed_block ? `#${summary.summary.latest_indexed_block}` : '-';

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${config.accent} 0%, #ffffff 64%)`,
          borderColor: 'divider'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Avatar sx={{ width: 72, height: 72, bgcolor: config.color, fontSize: '1.35rem', fontWeight: 900 }}>
            {config.displayName.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip size="small" color="primary" label={ui(config.displayName)} />
              <Chip size="small" variant="outlined" label={ui(config.ledgerScope)} />
              <Chip size="small" variant="outlined" label={t("API keys hidden")} />
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.15 }}>{t("My Info")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 860 }}>
              {ui(config.roleSummary)}{" "}{t("This page shows the account context, active integration contract, Gateway mapping, and indexed ledger scope for this organization.")}</Typography>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: 260 } }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.78)' }}>
              <KeyValue label={t("Signed-in user")} value={user.displayName || user.username} />
              <Divider sx={{ my: 1.25 }} />
              <KeyValue label={t("Login time")} value={formatDateTime(user.loginTime)} />
            </Paper>
          </Box>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{uiError(error)}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard label={t("Business Records")} value={businessRecords.toLocaleString()} helper={t("Scoped records indexed for this organization")} />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard label={t("Latest Scoped Block")} value={latestBlock} helper={t("Most recent indexed block in this scope")} />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard label={t("Hash-ref Records")} value={hashRefRecords.toLocaleString()} helper={t("Records with payload hash and payload reference")} />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard label={t("Latest Record Time")} value={formatDateTime(latestRecord?.block_created_at)} helper={t("From PostgreSQL block index")} />
        </Grid>

        <Grid item xs={12} lg={5}>
          <InfoCard title={t("Organization Scope")} icon={<LedgerIcon color="primary" />}>
            <Grid container spacing={1.75}>
              <Grid item xs={12} sm={6}><KeyValue label={t("Organization")} value={config.organizationName} /></Grid>
              <Grid item xs={12} sm={6}><KeyValue label={t("MSP / Org label")} value={config.mspLabel} mono /></Grid>
              <Grid item xs={12} sm={6}><KeyValue label={t("Ledger scope")} value={config.ledgerScope} mono /></Grid>
              <Grid item xs={12} sm={6}><KeyValue label={t("Channel")} value="socfai-production" mono /></Grid>
              <Grid item xs={12}><KeyValue label={t("Business chaincode")} value="supplychain / RecordIntegrationResult" mono /></Grid>
            </Grid>
          </InfoCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <InfoCard title={t("Integration Access")} icon={<LinkIcon color="primary" />}>
            <Grid container spacing={1.75}>
              <Grid item xs={12}>
                <KeyValue label={t("Endpoint")} value={ui(config.endpoint)} mono />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <KeyIcon color="warning" fontSize="small" />
                  <KeyValue label={t("Authentication")} value={t("X-API-Key required; key values are not shown here.")} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon color="success" fontSize="small" />
                  <KeyValue label={t("Status")} value={ui(config.endpointStatus)} />
                </Stack>
              </Grid>
            </Grid>
          </InfoCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <InfoCard title={t("Payload Contract")} icon={<PayloadIcon color="primary" />}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>{t("Required fields")}</Typography>
                <Box sx={{ mt: 0.75 }}>
                  <ChipGroup fields={config.requiredFields} color="primary" />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>{t("Preserved fields")}</Typography>
                <Box sx={{ mt: 0.75 }}>
                  <ChipGroup fields={config.preservedFields} />
                </Box>
              </Box>
              <Alert icon={<RuleIcon />} severity="info" sx={{ py: 0.75 }}>{t("The Gateway validates only the minimum fields needed for ledger mapping. Additional partner fields are preserved.")}</Alert>
            </Stack>
          </InfoCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <InfoCard title={t("Gateway Mapping")} icon={<HubIcon color="primary" />}>
            <Grid container spacing={1.25}>
              {config.mappingRows.map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
                    <KeyValue label={ui(label)} value={ui(value)} mono={label !== 'recordType'} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </InfoCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <InfoCard title={t("Visible Operations")} icon={<VerifiedIcon color="success" />}>
            {loading && !summary ? (
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">{t("Loading indexed scope.")}</Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {config.visibleOperations.map((operation) => {
                  const count = operationCount(summary, operation);
                  return (
                    <Chip
                      key={operation}
                      size="small"
                      color={count > 0 ? (isValidationOperation(operation) ? 'warning' : 'success') : 'default'}
                      variant={count > 0 ? 'filled' : 'outlined'}
                      label={ui(`${ui(operationDisplayLabel(operation))} ${count}`)}
                    />
                  );
                })}
              </Stack>
            )}
          </InfoCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <InfoCard title={t("Latest Indexed Record")} icon={<StorageIcon color="primary" />}>
            {latestRecord ? (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><KeyValue label={t("Flow")} value={`${latestRecord.sender || '-'} -> ${latestRecord.receiver || '-'}`} /></Grid>
                <Grid item xs={12} sm={6}><KeyValue label={t("Block")} value={latestRecord.block_number ? `#${latestRecord.block_number}` : '-'} /></Grid>
                <Grid item xs={12} sm={6}><KeyValue label={t("Business ID")} value={`${latestRecord.business_id_type || 'id'}: ${latestRecord.business_id || '-'}`} mono /></Grid>
                <Grid item xs={12} sm={6}><KeyValue label={t("Operation")} value={latestRecord.operation_type || '-'} /></Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">{t("No indexed records in this scope.")}</Typography>
            )}
          </InfoCard>
        </Grid>

        <Grid item xs={12}>
          <Alert icon={<SecurityIcon />} severity="success">{t("Raw partner payloads are stored in PostgreSQL. The blockchain ledger stores the common business record and hash-ref evidence fields such as payloadHash, payloadRef, and payloadSizeBytes.")}</Alert>
        </Grid>
      </Grid>
    </Box>
  );
}
